import { useNavigate } from '@tanstack/react-router';
import { Button } from '@components/ui';
import { useTranslation } from '@hooks/useTranslation';
import styles from './ReviewerEndedScreen.module.scss';

export type ReviewerEndReason =
  | 'completed'
  | 'cancelled'
  | 'not-a-reviewer'
  | 'removed'
  | 'not-in-review';

interface ReviewerEndedScreenProps {
  reason: ReviewerEndReason;
  backTo: string;
}

/**
 * Shown when the review session cannot continue — either already done,
 * the caller isn't a reviewer, or the snapshot isn't in review anymore.
 * Pure presentational — the caller decides which reason applies based on
 * the session/snapshot/my-review state (see useReviewerSession).
 */
export function ReviewerEndedScreen({ reason, backTo }: ReviewerEndedScreenProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { title, body, iconClass } = getCopy(reason, t);

  return (
    <div className={styles.container}>
      <svg
        className={`${styles.icon} ${iconClass}`}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        {reason === 'completed' ? (
          <path
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : (
          <path
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>

      <h1 className={styles.title}>{title}</h1>
      <p className={styles.body}>{body}</p>
      <Button variant="outline" onClick={() => navigate({ to: backTo })}>
        {t('K_REVIEWER_ENDED_BACK', 'Back to Contracts')}
      </Button>
    </div>
  );
}

function getCopy(reason: ReviewerEndReason, t: (key: string, fallback: string) => string) {
  switch (reason) {
    case 'completed':
      return {
        title: t('K_REVIEWER_ENDED_COMPLETED_TITLE', 'Review complete'),
        body: t(
          'K_REVIEWER_ENDED_COMPLETED_BODY',
          'Thank you. This review has been completed. No further changes can be made.',
        ),
        iconClass: styles.iconCompleted ?? '',
      };
    case 'cancelled':
      return {
        title: t('K_REVIEWER_ENDED_CANCELLED_TITLE', 'Review cancelled'),
        body: t('K_REVIEWER_ENDED_CANCELLED_BODY', 'This review was cancelled by the author.'),
        iconClass: styles.iconCancelled ?? '',
      };
    case 'removed':
      return {
        title: t('K_REVIEWER_REMOVED_TITLE', 'Removed from review'),
        body: t('K_REVIEWER_REMOVED_BODY', 'You are no longer a reviewer on this contract.'),
        iconClass: styles.iconRemoved ?? '',
      };
    case 'not-a-reviewer':
      return {
        title: t('K_REVIEWER_ENDED_NOT_A_REVIEWER', 'You are not a reviewer on this contract.'),
        body: t(
          'K_REVIEWER_ENDED_NOT_A_REVIEWER_BODY',
          'This review link is not addressed to you. If you believe this is a mistake, please contact the author.',
        ),
        iconClass: '',
      };
    case 'not-in-review':
      return {
        title: t('K_REVIEWER_ENDED_NOT_IN_REVIEW', 'This contract is no longer in review.'),
        body: t(
          'K_REVIEWER_ENDED_NOT_IN_REVIEW_BODY',
          'The review has ended. You can view the latest version of the document from the contracts list.',
        ),
        iconClass: '',
      };
  }
}
